export type Role = 'admin' | 'member' | 'catalogue_manager';

export interface Vertical {
  id: number;
  name: string;
  slug: string;
  settings: Record<string, unknown> | null;
}

export interface Paginated<T> {
  results: T[];
  count: number;
  page: number;
  per_page: number;
}

// Quote-domain verticals — plain slugs on Quotation.vertical, independent of
// the `verticals` table above (see internal/models/quote.go's doc comment).
// Seeded 1:1 with the same slugs, but nothing enforces that at the schema
// level.
export const QUOTE_VERTICALS = ['hotel', 'builder', 'export', 'lighting', 'retail', 'dealers'] as const;
export type QuoteVertical = (typeof QUOTE_VERTICALS)[number];

export const QUOTE_VERTICAL_META: Record<QuoteVertical, { label: string; icon: string; blurb: string }> = {
  hotel: { label: 'Hotel', icon: 'building-2', blurb: 'Guest-room automation & GRMS for hotels' },
  builder: { label: 'Builder', icon: 'blocks', blurb: 'Multi-unit residential projects' },
  export: { label: 'Export', icon: 'ship', blurb: 'International orders & projects' },
  lighting: { label: 'Lighting', icon: 'lightbulb', blurb: 'DALI & wireless lighting control' },
  retail: { label: 'Retail', icon: 'shopping-bag', blurb: 'Over-the-counter retail orders' },
  dealers: { label: 'Dealers', icon: 'handshake', blurb: 'Channel & dealer orders' }
};

export type QuotationStatus = 'draft' | 'submitted' | 'priced' | 'approved' | 'revised';

// Legal seller entity a quote bills under — picks the seller block
// (name/address/GST/email) internal/pdf prints. NOT derivable from vertical
// (see internal/models.BillingEntity* consts). Only an admin may set this
// (Rails: quotation_params admin-only permit).
export const BILLING_ENTITIES = ['controls', 'electric'] as const;
export type BillingEntity = (typeof BILLING_ENTITIES)[number];

export const BILLING_ENTITY_LABEL: Record<BillingEntity, string> = {
  controls: 'Toyama Controls & Systems Pvt Ltd.',
  electric: 'Toyama Electric Limited'
};

export interface Project {
  id: number;
  name: string;
  client_name: string;
  location: string;
  created_by_id: number;
  created_at: string;
  updated_at: string;
}

export interface Quotation {
  id: number;
  project_id: number | null;
  quotation_number: string;
  formatted_number: string;
  version: number;
  status: QuotationStatus;
  vertical: QuoteVertical;
  project_based: boolean;
  billing_entity: BillingEntity;
  currency: string;
  client_name: string;
  client_email: string;
  project_name: string;
  project_location: string;
  delivery_terms: string;
  validity_days: number;
  notes: string;
  source_quotation_id: number | null;
  root_quotation_id: number | null;
  lot_number: number;
  lot_label: string;
  subtotal_minor: number;
  discount_total_minor: number;
  installation_percent_bps: number;
  installation_amount_minor: number;
  commissioning_percent_bps: number;
  commissioning_amount_minor: number;
  special_discount_percent_bps: number;
  special_discount_amount_minor: number;
  tax_rate_bps: number;
  tax_amount_minor: number;
  grand_total_minor: number;

  // Internal margin giveaway (see internal/pricing/commission.go). Only
  // partner_commission_bps is stored; everything else here is derived
  // server-side from the persisted columns and is INTERNAL ONLY — it is not
  // part of the customer price chain and never appears on the client PDF or
  // the public share view.
  partner_commission_bps: number;
  list_subtotal_minor: number;
  customer_discount_minor: number;
  customer_discount_bps: number;
  partner_commission_minor: number;
  combined_given_bps: number;
  combined_given_minor: number;

  submitted_at: string | null;
  priced_at: string | null;
  approved_at: string | null;
  returned_for_pricing_at: string | null;
  archived_at: string | null;
  created_by_id: number;
  priced_by_id: number | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  designer_priced: boolean;
  share_token: string | null;
  share_password_protected: boolean;
}

export interface QuotationSection {
  id: number;
  quotation_id: number;
  name: string;
  section_type: string;
  quantity: number;
  position: number;
  subtotal_minor: number;
  discount_amount_minor: number;
  special_price_override_minor: number | null;
  section_total_minor: number;
  line_items: LineItem[];
}

export interface LineItem {
  id: number;
  quotation_section_id: number;
  product_id: number | null;
  description: string;
  part_number: string;
  quantity: number;
  position: number;
  unit_price_minor: number;
  net_price_minor: number;
  discount_fixed_minor: number;
  discount_percent_bps: number;
  line_total_minor: number;
  notes: string;
}

export interface QuotationTerm {
  id: number;
  quotation_id: number;
  // null for a custom term typed directly onto the quote (no backing template).
  terms_template_id: number | null;
  category: string;
  title: string;
  body: string;
  position: number;
}

export interface TermsTemplate {
  id: number;
  name: string;
  category: string;
  body: string;
  vertical: string;
  is_default: boolean;
}

export const TERMS_CATEGORIES = ['payment', 'delivery', 'installation', 'warranty', 'general', 'bank_details'] as const;
export type TermsCategory = (typeof TERMS_CATEGORIES)[number];

export interface QuotationDetail extends Quotation {
  sections: QuotationSection[];
  terms: QuotationTerm[];
}

export interface ClientComment {
  id: number;
  quotation_id: number;
  author_name: string;
  author_email: string;
  body: string;
  read_at: string | null;
  created_at: string;
}

// QuotationVersion is one row from the admin-only version-history list
// (GET /quotations/:id/versions) — metadata is only present when the
// version's snapshot JSON could be parsed (always true in practice; see
// versionJSON's doc comment in internal/api/handlers/quotation_versions.go).
export interface QuotationVersion {
  id: number;
  version_number: number;
  formatted_version: string;
  created_at: string;
  change_summary: string;
  pdf_available: boolean;
  metadata?: {
    created_by: string;
    sections_count: number;
    line_items_count: number;
  };
}

export interface ShareInfo {
  share_token: string | null;
  password_protected: boolean;
}

// SharedQuotation is the trimmed, client-facing view served by the public
// (unauthenticated) share-link route — see internal/api/handlers/share_public.go's
// shareQuoteDetailJSON. Deliberately narrower than QuotationDetail.
export interface SharedQuotation {
  id: number;
  quotation_number: string;
  formatted_number: string;
  version: number;
  status: QuotationStatus;
  vertical: QuoteVertical;
  currency: string;
  client_name: string;
  project_name: string;
  project_location: string;
  delivery_terms: string;
  validity_days: number;
  subtotal_minor: number;
  discount_total_minor: number;
  installation_amount_minor: number;
  commissioning_amount_minor: number;
  special_discount_amount_minor: number;
  tax_rate_bps: number;
  tax_amount_minor: number;
  grand_total_minor: number;
  approved_at: string | null;
  created_at: string;
  sections: QuotationSection[];
  terms: QuotationTerm[];
  comments: ClientComment[];
}

export interface Product {
  id: number;
  name: string;
  part_number: string;
  description: string;
  unit_of_measure: string;
  requires_driver: boolean;
  source: string;
  ownership: string | null;
  active: boolean;
  // Internal cost — the key is only present at all when the caller is an
  // an admin or catalogue manager on a management route, so this
  // must stay optional rather than `number | null`.
  base_cost_price_minor?: number | null;
  list_price_minor: number | null;
  category: string;
  // Optional for older API responses/fixtures; the current API returns [].
  additional_categories?: string[];
  functional_category: string;
  functional_type: string;
  syncs_to_rcu: boolean;
  // Set by the admin product-edit path only (see adminUpdateProduct's doc
  // comment) — null until an admin has hand-edited name/category/price.
  last_manual_edit_at: string | null;
}

// ProductList is the plain, unpaginated {results, count} shape shared by the
// catalog hygiene endpoints (review/stale) — see handlers/catalog_hygiene.go.
export interface ProductList {
  results: Product[];
  count: number;
}

// FastMover / analytics response shapes — see catalogAnalytics in
// handlers/catalog_hygiene.go.
export interface FastMover {
  id: number;
  name: string;
  part_number: string;
  category: string;
  count: number;
  last_quoted: string;
}

export interface MarginByOwnership {
  ownership: string | null;
  avg_margin_percent: number;
}

export interface CatalogAnalytics {
  window_days: number;
  fast_movers: FastMover[];
  not_quoted_in_window: { count: number; results: Product[] };
  margin_by_ownership: MarginByOwnership[];
}

// CatalogIssues mirrors catalogIssues' response — the triage inbox
// aggregating every hygiene category. issues_count is the badge total
// (duplicate-group MEMBER counts + review + rcu conflicts; stale excluded).
export interface CatalogIssues {
  duplicate_groups: DuplicateGroup[];
  rcu_category_conflicts: Product[];
  review_products: Product[];
  stale_count: number;
  issues_count: number;
}

// CatalogCategory is one row of the admin catalog's category grid — see
// GET /admin/catalog/categories. `group`/`subgroup` are parsed server-side
// from the "Group · Subgroup" convention (parseCategoryLabel in catalog.go)
// so the frontend never re-parses the raw category string.
export interface CatalogCategory {
  category: string;
  uncategorized: boolean;
  group: string;
  subgroup: string;
  count: number;
  missing_taxonomy: number;
  missing_ownership: number;
  missing_price: number;
  complete_count: number;
  family: string;
  family_label: string;
  family_description: string;
}

export interface CatalogHealth {
  total: number;
  uncategorised: number;
  missing_taxonomy: number;
  missing_ownership: number;
  missing_price: number;
  inactive: number;
}

// ImportRow is one classified CSV row from the catalog CSV importer — see
// importRow in handlers/catalog_import.go. Both the preview and commit
// endpoints return the same shape so the confirm step shows exactly what the
// dry run promised.
export interface ImportRow {
  row: number;
  part_number: string;
  name: string;
  // "conflict": the incoming list_price differs from the current one AND an
  // admin has manually edited this product's price/identity since (see
  // Product.last_manual_edit_at) — see catalogImportCommit's doc comment.
  change: 'create' | 'update' | 'unchanged' | 'conflict' | 'error';
  error?: string;
  current?: Record<string, unknown>;
  incoming?: Record<string, unknown>;
}

export interface ImportResult {
  results: ImportRow[];
  summary: {
    create: number;
    update: number;
    unchanged: number;
    conflict: number;
    error: number;
    // Only present on a commit response: how many "conflict" rows were
    // skipped because `force` wasn't set.
    skipped_conflicts?: number;
  };
}

// DuplicateProduct/DuplicateGroup mirror catalog_merge.go's duplicateProduct
// and catalogDuplicates response — active products whose part numbers
// normalize to the same key (case/space/dash/punctuation-insensitive).
export interface DuplicateProduct {
  id: number;
  part_number: string;
  name: string;
  category: string;
}

export interface DuplicateGroup {
  key: string;
  products: DuplicateProduct[];
}

export interface MergePreview {
  winner: DuplicateProduct;
  losers: DuplicateProduct[];
  line_items: number;
}

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: Role;
  vertical_id: number | null;
  verticals: number[];
  totp_enabled: boolean;
  giveaway_cap_bps: number;
  last_active: string | null;
  created_at: string;
  temp_password?: string;
}

// ActivityLogEntry mirrors internal/models.ActivityLog's sparse 5-event
// audit trail (see internal/api/handlers/activity_log.go) — quotation_id is
// deliberately not surfaced here since the admin feed doesn't link back to
// the quotation, only shows what happened.
export interface ActivityLogEntry {
  id: number;
  action: string;
  description: string;
  created_at: string;
  user_name: string | null;
  metadata: Record<string, unknown>;
}

export interface LoginEvent {
  id: number;
  user_id: number;
  email?: string;
  created_at: string;
  ip_address: string;
  device: string;
  browser: string;
  os: string;
}

export interface Me {
  user: {
    id: number;
    email: string;
    name: string;
    role: Role;
    vertical_id: number | null;
    totp_enabled: boolean;
    giveaway_cap_bps: number;
  };
  verticals: Vertical[];
}
